import { combineReducers, Action, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  LubavSessionType,
  LubavOfficeHoursSubsessionType,
  StructuredSessionId,
} from '@backrs_ui/grow-ui/src/models/models';

import { boxDataReducer } from '../boxData/boxData';
import { coachLubavReducer } from '../coachLubav/coachLubav';
import { logout } from '../session/logoutThunk';
import { solutionSessionReducer } from '../solutionSession/solutionSession';

// combined reducer for session-specific state
const sessionSpecificReducer = combineReducers({
  coachLubav: coachLubavReducer,
  boxData: boxDataReducer,
  solutionSession: solutionSessionReducer,
});

export type SessionStateMap = Record<LubavSessionType, ReturnType<typeof sessionSpecificReducer>>;

export type QueuedPrompt = Readonly<{
  label: string;
  chatMessage?: string;
  prompt: string;
  timestamp: number;
  promptButtonId?: string;
}>;

type SessionState = Readonly<{
  activeSessionType: LubavSessionType | null;
  officeHoursSubsessionType: LubavOfficeHoursSubsessionType | null;
  queuedPrompt: QueuedPrompt | null;
  pendingSessionSwitch: LubavSessionType | null;
  pendingStructuredChoice: LubavSessionType | null;
  previewedSession: StructuredSessionId | null;
  loadingSessionData: LubavSessionType | null;
  mobileFabOpen: boolean;
  sessions: SessionStateMap;
}>;

const initialState: SessionState = {
  activeSessionType: null,
  officeHoursSubsessionType: null,
  queuedPrompt: null,
  pendingSessionSwitch: null,
  pendingStructuredChoice: null,
  previewedSession: null,
  loadingSessionData: null,
  mobileFabOpen: false,
  sessions: {},
};

const sessionSlice = createSlice({
  name: 'lubavSession',
  initialState,
  reducers: {
    setActiveSessionType: (state, action: PayloadAction<LubavSessionType | null>) => {
      state.activeSessionType = action.payload;
    },
    setOfficeHoursSubsessionType: (
      state,
      action: PayloadAction<LubavOfficeHoursSubsessionType | null>,
    ) => {
      state.officeHoursSubsessionType = action.payload;
    },
    setQueuedPrompt: (state, action: PayloadAction<QueuedPrompt>) => {
      state.queuedPrompt = action.payload;
    },
    clearQueuedPrompt: (state) => {
      state.queuedPrompt = null;
    },
    requestSessionSwitch: (state, action: PayloadAction<LubavSessionType>) => {
      state.pendingSessionSwitch = action.payload;
    },
    clearSessionSwitch: (state) => {
      state.pendingSessionSwitch = null;
    },
    setPendingStructuredChoice: (state, action: PayloadAction<LubavSessionType | null>) => {
      state.pendingStructuredChoice = action.payload;
    },
    // A structured session shown in the right panel (its tab and banner) while the user decides
    // whether to enter it. Never activated, so no session is created or loaded for it.
    setPreviewedSession: (state, action: PayloadAction<StructuredSessionId | null>) => {
      state.previewedSession = action.payload;
    },
    setLoadingSessionData: (state, action: PayloadAction<LubavSessionType | null>) => {
      state.loadingSessionData = action.payload;
    },
    openMobileFab: (state) => {
      state.mobileFabOpen = true;
    },
    closeMobileFab: (state) => {
      state.mobileFabOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // LOGOUT
      .addCase(logout, (state) => {
        state.activeSessionType = null;
        state.officeHoursSubsessionType = null;
        state.queuedPrompt = null;
        state.pendingSessionSwitch = null;
        state.pendingStructuredChoice = null;
        state.previewedSession = null;
        state.loadingSessionData = null;
        state.mobileFabOpen = false;
        state.sessions = {};
      });
  },
});

export const {
  setActiveSessionType,
  setOfficeHoursSubsessionType,
  setQueuedPrompt,
  clearQueuedPrompt,
  requestSessionSwitch,
  clearSessionSwitch,
  setPendingStructuredChoice,
  setPreviewedSession,
  setLoadingSessionData,
  openMobileFab,
  closeMobileFab,
} = sessionSlice.actions;

export const sessionStateReducer = (
  state: SessionState = initialState,
  action: Action &
    Partial<{
      sessionType: LubavSessionType;
      meta?: { arg?: { activeSession?: LubavSessionType } };
    }>,
): SessionState => {
  const sliceState = sessionSlice.reducer(state, action);

  if (sliceState !== state) {
    return sliceState;
  }

  // get session type from either direct property (for actions) or metadata (for thunks)
  const sessionType = action.sessionType || action.meta?.arg?.activeSession;

  if (!sessionType) return state;

  const newSessionState = sessionSpecificReducer(state.sessions[sessionType], {
    ...action,
    sessionType,
  });

  if (state.sessions[sessionType] === newSessionState) {
    return state;
  }

  return {
    ...state,
    sessions: { ...state.sessions, [sessionType]: newSessionState },
  };
};
